import { useState, useEffect, useCallback } from 'react';
import translateService, { SUPPORTED_LANGUAGES, t as translate } from '../services/translateService';

/**
 * Hook to use translation service in React components
 */
export function useTranslate() {
  const [currentLanguage, setCurrentLanguage] = useState(translateService.getLanguage());
  const [isReady, setIsReady] = useState(false);
  const [, forceUpdate] = useState(0);

  useEffect(() => {
    // Initialize translation service
    const lang = translateService.init();
    setCurrentLanguage(lang);
    setIsReady(true);

    // Subscribe to language changes
    const unsubscribe = translateService.subscribe((newLang) => {
      setCurrentLanguage(newLang);
      // Force re-render to update all translations
      forceUpdate(n => n + 1);
    });

    return unsubscribe;
  }, []);

  const setLanguage = useCallback((langCode) => {
    return translateService.setLanguage(langCode);
  }, []);

  const getLanguageInfo = useCallback((langCode) => {
    return translateService.getLanguageInfo(langCode);
  }, []);

  const isRTL = useCallback((langCode) => {
    return translateService.isRTL(langCode);
  }, []);

  // Translation function - this will use current language
  const t = useCallback((key, params) => {
    return translateService.t(key, params);
  }, [currentLanguage]); // Re-create when language changes

  // eslint-disable-next-line no-unused-vars
  const _rerenderToken = currentLanguage;

  return {
    currentLanguage,
    setLanguage,
    getLanguageInfo,
    isRTL: translateService.isRTL(),
    isReady,
    languages: translateService.getEnabledLanguages(),
    translationsEnabled: translateService.translationsEnabled,
    t  // Translation function
  };
}

export { translate as t };
export default useTranslate;
